// shared-lib/cabinet-runs/overrideHomes.js
//
// Where a WIDTH-LESS override lives this solve (plan 2026-10-21, C7).
//
// A width-less override — a front, an upper's height, an open shelf, an
// appearance — is stored as a point on the wall (`anchorParam`, the centre of
// the box the user clicked), and the box under that point wears it. That is
// right for the first solve and wrong after any re-pack that moves the seams:
// narrow the cabinet beside it and the point can land in the NEIGHBOUR, which
// then wears the drawer front or the 15" height the user set on another box.
//
// So the point is only the FIRST seat. Once a box wears the record (the fill
// stamps `overrideId` on it), that box is the record's home, and the next
// solve seats the record on the new box that overlaps the old one most. The
// stored `anchorParam` is not rewritten: the wearing box's `overrideId` is
// the record, and it is read back from the room's objects every solve. A
// fixed-width override is untouched — it already IS its own box.
//
// Pure.

const { isUpperTier } = require('./ownership');
const { runAliases } = require('./runIds');
const { overridesForRun, overridesForSegment } = require('./bayOverrides');
const { worldToWallParam } = require('../kitchen-assembly/wallMath');
const { SPAN_EPS } = require('./spanPacking');

/** The packer's own span tolerance, so "inside a box" means what it does there. */
const EPS = SPAN_EPS;

const overlap = (a, b) => Math.min(a.end, b.end) - Math.max(a.start, b.start);
const holds = (box, param) =>
  param >= box.start - EPS && param <= box.end + EPS;

/** Is `o` a box of this tier, as a previous solve built it? */
function inTier(o, tier) {
  return tier === 'upper' ? isUpperTier(o) : o?.type === 'lower-cabinet';
}

/**
 * The spans (wall params) of this run's boxes on segment `segIndex`'s wall
 * that the LAST solve stamped with an override, in `tier` — read off the
 * room's objects. Keyed by `overrideId`; a record two boxes wore (a split) is
 * their union. A stacked box (`category.stack`) is never a record's home.
 *
 * @returns {Map<string, { start: number, end: number }>}
 */
function previousSpansOf(run, segIndex, tier, existingObjects) {
  const out = new Map();
  const wall = run?.segments?.[segIndex]?.wall;
  if (!wall?.id || !wall.alignment || !existingObjects?.length) return out;
  const ids = new Set([run.id, ...runAliases(run)]);
  for (const o of existingObjects) {
    if (!o?.overrideId || !ids.has(o.runId)) continue;
    if (o.category?.stack || !inTier(o, tier)) continue;
    if (!(o.parentWallIds || []).includes(wall.id)) continue;
    if (!o.center || !(o.dimensions?.x > 0)) continue;
    const p = worldToWallParam(wall, o.center);
    const half = o.dimensions.x / 2;
    const prev = out.get(o.overrideId);
    out.set(o.overrideId, {
      start: Math.min(p - half, prev ? prev.start : Infinity),
      end: Math.max(p + half, prev ? prev.end : -Infinity),
    });
  }
  return out;
}

/**
 * Where `override` is seated among `boxes` (`{ start, end }`, wall params):
 * the centre of the box overlapping `prevSpan` — the box that wore it last
 * solve — most; a tie goes to the box holding the stored point. The stored
 * `anchorParam` when there is no previous wearer or nothing overlaps it.
 */
function homeAnchor(boxes, override, prevSpan) {
  if (!prevSpan) return override.anchorParam;
  const point = override.anchorParam;
  let best = null;
  let bestOverlap = EPS;
  for (const box of boxes) {
    const o = overlap(box, prevSpan);
    if (o > bestOverlap + EPS) {
      best = box;
      bestOverlap = o;
    } else if (
      best &&
      Math.abs(o - bestOverlap) <= EPS &&
      holds(box, point) &&
      !holds(best, point)
    ) {
      best = box;
    }
  }
  return best ? (best.start + best.end) / 2 : point;
}

/**
 * The wall param a width-less record is seated by among `boxes` — the boxes
 * it may govern: its home (`homeAnchor`) when its stored point stands in one
 * of them, else the stored point itself. A point in none of them names
 * something else that reads it there — an appliance's base, a corner — and a
 * record is never moved off it.
 */
function seatParam(boxes, override, prevSpan) {
  if (!boxes.some((b) => holds(b, override.anchorParam)))
    return override.anchorParam;
  return homeAnchor(boxes, override, prevSpan);
}

/**
 * `run` with segment `segIndex`'s width-less overrides in `tier` seated on
 * their homes among `boxes` — the boxes a width-less record may govern (the
 * run-sized ones). The same object when no record moves to a different box
 * than its stored point already names, so a run whose seams did not move is
 * byte-identical. A record whose stored point is in none of `boxes` is left
 * where it is: it governs something else — an appliance's base, a corner —
 * that reads it by its point.
 */
function homedRun(run, segIndex, tier, boxes, prevSpans) {
  if (!prevSpans?.size) return run;
  const moved = new Map();
  for (const o of overridesForSegment(run, segIndex, tier)) {
    if (o.width != null) continue;
    const prev = prevSpans.get(o.id);
    if (!prev) continue;
    const at = seatParam(boxes, o, prev);
    const now = boxes.find((b) => holds(b, o.anchorParam));
    if (!now || holds(now, at)) continue;
    moved.set(o.id, at);
  }
  if (!moved.size) return run;
  return {
    ...run,
    bayOverrides: overridesForRun(run).map((o) =>
      moved.has(o.id) ? { ...o, anchorParam: moved.get(o.id) } : o
    ),
  };
}

module.exports = { previousSpansOf, homeAnchor, seatParam, homedRun };
